import React, { FunctionComponent, useMemo } from 'react'
import Image from 'next/image'
import { Icon, useIsCashTheme } from '@afterpaytouch/core'
import { OrderPaymentSchedule } from '@afterpaytouch/portal-api/consumer/ordertransactions/types'
import { useTranslation } from 'next-i18next'
import { getScheduleIcon } from './utils'
import { OrderScheduleIcons } from './constants'
import { DotIcon } from '../Icon/Dot/DotIcon'

const I18N_NAMESPACE = ['common', 'orders']

interface OrderScheduleIconProps {
  installment: OrderPaymentSchedule
  isNextDue: boolean
}

const testNameSpace = 'order-schedule-icon'

const OverdueIcon: FunctionComponent<{ isCashTheme: boolean }> = ({ isCashTheme }) =>
  isCashTheme ? (
    <DotIcon kind='Light' size='Large' />
  ) : (
    <span data-testid={testNameSpace} className='bg-light relative block rounded-full'>
      <Icon.WarningAlt color='Fire' />
    </span>
  )

const CancelledIcon: FunctionComponent<{ isCashTheme: boolean }> = ({ isCashTheme }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  return isCashTheme ? (
    <DotIcon kind='Subtle' size='Large' />
  ) : (
    <Image
      data-testid={testNameSpace}
      width={24}
      height={24}
      src={`/images/cancelled-icon.svg`}
      alt={t('orders:orderSchedule:graphic:cancelled')}
      style={{
        maxWidth: '100%',
        height: 'auto',
      }}
    />
  )
}

const RefundIcon: FunctionComponent<{ isCashTheme: boolean }> = ({ isCashTheme }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  return isCashTheme ? (
    <DotIcon kind='Subtle' size='Large' />
  ) : (
    <Image
      data-testid={testNameSpace}
      width={24}
      height={24}
      src={`/images/refund-icon.svg`}
      alt={t('orders:orderSchedule:graphic:refund')}
      style={{
        maxWidth: '100%',
        height: 'auto',
      }}
    />
  )
}

const OwedIcon: FunctionComponent<{ isCashTheme: boolean }> = ({ isCashTheme }) =>
  isCashTheme ? (
    <DotIcon kind='Subtle' size='Large' />
  ) : (
    <div className='bg-light'>
      <DotIcon type='Hollow' kind='Subtle' size='Small' />
    </div>
  )

const CompleteIcon: FunctionComponent<{ isCashTheme: boolean }> = ({ isCashTheme }) =>
  isCashTheme ? (
    <DotIcon kind='Dark' size='Large' />
  ) : (
    <div data-testid={testNameSpace} className='bg-primary relative flex h-6 w-6 items-center justify-center rounded-full'>
      <Icon.Check size='Mini' />
    </div>
  )

const NextDueIcon: FunctionComponent = () => <DotIcon kind='Light' size='Large' />

const IconMap: Record<OrderScheduleIcons, React.FC<{ isCashTheme: boolean }>> = {
  [OrderScheduleIcons.Completed]: CompleteIcon,
  [OrderScheduleIcons.Refunded]: RefundIcon,
  [OrderScheduleIcons.Cancelled]: CancelledIcon,
  [OrderScheduleIcons.Overdue]: OverdueIcon,
  [OrderScheduleIcons.Owed]: OwedIcon,
  [OrderScheduleIcons.NextDue]: NextDueIcon,
}

export const OrderScheduleIcon: FunctionComponent<OrderScheduleIconProps> = ({ installment, isNextDue }) => {
  const isCashTheme = useIsCashTheme()
  const scheduleIcon = useMemo(() => getScheduleIcon(installment, isNextDue), [installment, isNextDue])

  const IconComponent = IconMap[scheduleIcon]
  return <IconComponent isCashTheme={isCashTheme} />
}
